import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "ota-management" }
const _hoisted_2 = { class: "firmware-section" }
const _hoisted_3 = { class: "card-header" }
const _hoisted_4 = { class: "header-actions" }
const _hoisted_5 = { class: "action-buttons" }
const _hoisted_6 = { class: "audio-section" }
const _hoisted_7 = { class: "card-header" }
const _hoisted_8 = {
  key: 0,
  class: "header-actions"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "audio-meta" }
const _hoisted_11 = { class: "audio-file" }
const _hoisted_12 = { class: "audio-meta" }
const _hoisted_13 = {
  key: 1,
  class: "audio-empty"
}
const _hoisted_14 = { class: "device-upgrade-section" }
const _hoisted_15 = { class: "card-header" }
const _hoisted_16 = { class: "header-filters" }
const _hoisted_17 = { key: 1 }
const _hoisted_18 = { class: "action-buttons" }
const _hoisted_19 = { class: "table-footer" }
const _hoisted_20 = { class: "dialog-footer" }
const _hoisted_21 = { class: "dialog-footer" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import { useStore } from "vuex";
import { DataService } from "../services/dataService";
import { logger } from "../utils/logger";

const KEYWORD_DEBOUNCE_MS = 300;

export default /*@__PURE__*/_defineComponent({
  __name: 'OTAManagement',
  setup(__props) {

const { Upload, UploadFilled, Refresh, Search } =
  ElementPlusIconsVue as unknown as Record<string, any>;

const store = useStore();
const hasPermission = (perm: string) => store.getters.hasPermission(perm);

const ASCII_FILENAME_REGEX = /^[\x21-\x7E]+$/;

// 版本号比较：返回 1(a>b)、-1(a<b)、0(a==b)
const normalizeVersion = (v: string): number[] => {
  if (!v) return [];
  const core = v.trim().replace(/^v/i, "");
  return core.split(".").map((p) => Number(p) || 0);
};

const compareVersion = (a: string, b: string): number => {
  const pa = normalizeVersion(a);
  const pb = normalizeVersion(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va > vb) return 1;
    if (va < vb) return -1;
  }
  return 0;
};

// 本文件为 OTA 管理页面，主要用于 OTA 设备列表展示。2024-07-24：统一接口字段，所有设备列表统一用 data.items。
// 组件数据
const showUploadDialog = ref(false);
const statusFilter = ref("");
const searchKeyword = ref("");
const currentPage = ref(1);
const pageSize = ref(20);

// 组件状态
const loading = ref(false);
const firmwareList = ref<any[]>([]);
const selectedFirmwareId = ref<number | null>(null);
const firmwareTableRef = ref();

// 设备列表
const deviceList = ref<any[]>([]);
const selectedDevices = ref<any[]>([]);
const deviceTableRef = ref<any>(null);
const keywordDebounceTimer = ref<number | null>(null);
let otaPollingTimer: number | null = null;

const cancelKeywordDebounce = () => {
  if (keywordDebounceTimer.value !== null) {
    window.clearTimeout(keywordDebounceTimer.value);
    keywordDebounceTimer.value = null;
  }
};

const clearDeviceSelection = () => {
  selectedDevices.value = [];
  void nextTick(() => {
    if (
      deviceTableRef.value &&
      typeof deviceTableRef.value.clearSelection === "function"
    ) {
      deviceTableRef.value.clearSelection();
    }
  });
};

const handleFilterChanged = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  fetchOTAData();
};

const handleKeywordInput = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  keywordDebounceTimer.value = window.setTimeout(() => {
    keywordDebounceTimer.value = null;
    fetchOTAData();
  }, KEYWORD_DEBOUNCE_MS);
};

const handleKeywordCleared = () => {
  cancelKeywordDebounce();
  clearDeviceSelection();
  fetchOTAData();
};

// 音频资源管理
const audioLoading = ref(false);
const audioAssets = ref<Record<string, any>>({ alarm: null, wait: null });
const audioTypeOptions = [
  { label: "告警音 (alarm)", value: "alarm" },
  { label: "等待音 (wait)", value: "wait" },
];
const audioTableData = computed(() =>
  audioTypeOptions.map((item) => ({
    ...item,
    asset: audioAssets.value[item.value],
  }))
);
const showAudioUploadDialog = ref(false);
const audioUploadLoading = ref(false);
const audioUploadForm = ref<{ audioType: string; file: File | null }>({
  audioType: "alarm",
  file: null,
});
const audioFileList = ref<any[]>([]);

const formatFileSize = (size?: number | null) => {
  if (!size || size <= 0) return "-";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const formatUploadTime = (time?: string | null) => {
  if (!time) return "-";
  try {
    return new Date(time).toLocaleString();
  } catch {
    return "-";
  }
};

const startOtaPolling = () => {
  if (otaPollingTimer !== null) {
    return;
  }
  otaPollingTimer = window.setInterval(() => {
    fetchOTAData();
  }, 5000);
};

const stopOtaPolling = () => {
  if (otaPollingTimer !== null) {
    clearInterval(otaPollingTimer);
    otaPollingTimer = null;
  }
};

// 获取OTA数据
const fetchOTAData = async () => {
  loading.value = true;
  try {
    // 并行获取固件列表和设备列表
    const [firmwareResponse, deviceResponse] = await Promise.all([
      DataService.getOTAFirmwareList(),
      DataService.getOTADeviceList({
        status: statusFilter.value,
        keyword: searchKeyword.value,
      }),
    ]);

    // 更新固件列表
    if (
      firmwareResponse &&
      typeof firmwareResponse === "object" &&
      "data" in firmwareResponse
    ) {
      const resp = firmwareResponse as { data: any };
      if (resp.data && resp.data.code === 0) {
        firmwareList.value = resp.data.data;
      }
    }

    // 更新设备列表（后端字段转换为前端驼峰命名并归一状态值）
    let items: any[] = [];
    if (
      deviceResponse &&
      typeof deviceResponse === "object" &&
      "data" in deviceResponse
    ) {
      const resp = deviceResponse as { data: any };
      if (resp.data && resp.data.code === 0) {
        const responseData = resp.data.data;
        if (Array.isArray(responseData)) {
          items = responseData.map((d: Record<string, any>) => {
            const rawStatus = d.upgrade_status || "";
            const normalizedStatus =
              rawStatus === "up_to_date" ? "latest" : rawStatus || "outdated";
            const finalStatus =
              normalizedStatus === "cancelled" ? "outdated" : normalizedStatus;

            return {
              device_id: d.device_id,
              deviceName: d.device_name,
              currentVersion: d.current_version,
              targetVersion: d.target_version,
              _backendTargetVersion: d.target_version,
              upgradeStatus: finalStatus,
              lastBackendStatus: normalizedStatus,
              upgradeProgress: d.upgrade_progress ?? 0,
              lastUpgradeTime: d.last_upgrade_time,
            };
          });
        }
      }
    }
    deviceList.value = items;
    clearDeviceSelection();

    const hasRunningTask = items.some((device) =>
      ["upgrading", "pending"].includes(device.upgradeStatus)
    );
    if (hasRunningTask) {
      startOtaPolling();
    } else {
      stopOtaPolling();
    }
  } catch (error) {
    logger.error("获取OTA数据失败:", error);
    ElMessage.error("获取数据失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};

const fetchAudioAssets = async () => {
  audioLoading.value = true;
  try {
    const response = await DataService.getOTAAudioAssets();
    if (response && response.data && response.data.code === 0) {
      audioAssets.value = response.data.data || { alarm: null, wait: null };
    } else {
      audioAssets.value = { alarm: null, wait: null };
      ElMessage.error(response?.data?.message || "获取音频资源失败");
    }
  } catch (error) {
    logger.error("获取音频资源失败:", error);
    ElMessage.error("获取音频资源失败，请稍后重试");
    audioAssets.value = { alarm: null, wait: null };
  } finally {
    audioLoading.value = false;
  }
};

// 上传表单
const uploadForm = ref({
  version: "",
  description: "",
  file: null,
});

const uploadRules = {
  version: [{ required: true, message: "请输入版本号", trigger: "blur" }],
  description: [{ required: true, message: "请输入版本描述", trigger: "blur" }],
};

const fileList = ref([]);
const uploadFormRef = ref();

// 过滤设备列表
const filteredDeviceList = computed(() => {
  let filtered = deviceList.value;

  if (statusFilter.value) {
    filtered = filtered.filter(
      (device) => device.upgradeStatus === statusFilter.value
    );
  }

  if (searchKeyword.value) {
    filtered = filtered.filter(
      (device) =>
        device.deviceName.includes(searchKeyword.value) ||
        device.device_id.includes(searchKeyword.value)
    );
  }

  return filtered;
});

const selectedFirmware = computed(() => {
  if (selectedFirmwareId.value === null) {
    return null;
  }
  return (
    firmwareList.value.find(
      (fw) => Number(fw.id) === Number(selectedFirmwareId.value)
    ) || null
  );
});

const canStartTargetedUpgrade = computed(
  () => selectedFirmware.value !== null && selectedDevices.value.length > 0
);

const handleFirmwareSelectionChange = (row: any) => {
  if (!row) {
    selectedFirmwareId.value = null;
    return;
  }
  selectedFirmwareId.value = Number(row.id);
};

const getFirmwareRowClass = ({ row }: { row: any }) => {
  return Number(row.id) === Number(selectedFirmwareId.value)
    ? "selected-firmware-row"
    : "";
};

const handleDeviceSelectionChange = (selection: any[]) => {
  selectedDevices.value = selection;
};

const selectedDeviceIdSet = computed(
  () => new Set(selectedDevices.value.map((d: any) => d.device_id))
);

const getDeviceTargetVersion = (device: any) => {
  const fw = selectedFirmware.value;
  if (fw && fw.version && selectedDeviceIdSet.value.has(device.device_id)) {
    return fw.version;
  }
  if (device._backendTargetVersion !== undefined) {
    return device._backendTargetVersion;
  }
  return device.targetVersion;
};

const startUpgradeForDevices = async (
  deviceIds: string[],
  options: { resetSelection?: boolean } = {}
) => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先选择目标固件版本");
    return false;
  }
  if (deviceIds.length === 0) {
    ElMessage.warning("请至少选择一台设备");
    return false;
  }

  loading.value = true;
  try {
    const payload = {
      firmwareId: selectedFirmwareId.value as number,
      device_ids: deviceIds,
    };

    const response = await DataService.startOTADeviceUpgrade(payload);

    if (response && response.data && response.data.code === 0) {
      const respData = response.data.data || {};
      const restartFailed: any[] = respData.restart_failed || [];
      const restartSuccess: string[] = respData.restart_success || [];
      const message =
        response.data.message ||
        (restartFailed.length > 0
          ? `升级任务已创建，成功重启 ${restartSuccess.length} 台，${restartFailed.length} 台失败`
          : `升级任务已创建，已重启 ${restartSuccess.length} 台设备`);
      if (restartFailed.length > 0) {
        ElMessage.warning(message);
      } else {
        ElMessage.success(message);
      }

      if (options.resetSelection) {
        clearDeviceSelection();
      }

      await fetchOTAData();
      return true;
    }

    ElMessage.error(response?.data?.message || "创建升级任务失败");
    return false;
  } catch (error) {
    logger.error("创建定向升级任务失败", error);
    ElMessage.error("创建升级任务失败，请稍后重试");
    return false;
  } finally {
    loading.value = false;
  }
};

const handleTargetedUpgrade = async () => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先选择目标固件版本");
    return;
  }
  if (selectedDevices.value.length === 0) {
    ElMessage.warning("请勾选至少一台设备");
    return;
  }

  const firmwareVersion = selectedFirmware.value.version;
  const deviceCount = selectedDevices.value.length;

  const hasDowngrade = selectedDevices.value.some(
    (device: any) => compareVersion(device.currentVersion, firmwareVersion) > 0
  );
  if (hasDowngrade) {
    ElMessage.warning(
      `批量升级仅支持从低版本升级到高版本。如需将更高版本降级到 ${firmwareVersion}，请在设备行使用“升级”按钮单独操作。`
    );
    return;
  }

  const confirmMessage = `确定将 ${deviceCount} 台设备升级到版本 ${firmwareVersion} 吗？`;

  try {
    await ElMessageBox.confirm(confirmMessage, "确认升级", {
      type: "warning",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
    await startUpgradeForDevices(
      selectedDevices.value.map((device) => device.device_id),
      { resetSelection: true }
    );
  } catch (error) {
    if (error !== "cancel") {
      logger.error("创建定向升级任务失败", error);
      ElMessage.error("创建升级任务失败，请稍后重试");
    }
  }
};

// 获取状态类型
const getStatusType = (status: string) => {
  const typeMap: Record<
    string,
    "success" | "warning" | "primary" | "danger" | "info"
  > = {
    latest: "success",
    up_to_date: "success",
    pending: "warning",
    outdated: "warning",
    upgrading: "primary",
    failed: "danger",
  };
  return typeMap[status] || "info";
};

// 获取状态文本
const getStatusText = (status: string) => {
  const textMap: Record<string, string> = {
    latest: "最新版本",
    up_to_date: "最新版本",
    pending: "待升级",
    outdated: "需要升级",
    upgrading: "升级中",
    failed: "升级失败",
  };
  return textMap[status] || "未知";
};

// 刷新固件列表
const refreshFirmware = () => {
  ElMessage.success("固件列表已刷新");
};

// 设置当前固件版本
const setActiveFirmware = async (firmware: {
  version: string;
  id: string;
  status: string;
}) => {
  try {
    const isCurrent = firmware.status === "latest";
    const message = isCurrent
      ? `确定要将版本 ${firmware.version} 设为历史版本吗？`
      : `确定要将版本 ${firmware.version} 设为当前版本吗？`;

    await ElMessageBox.confirm(message, "确认操作", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });

    loading.value = true;

    // 调用后台API设置固件为当前版本
    const response = await DataService.setActiveFirmware(Number(firmware.id));

    if (response && response.data && response.data.code === 0) {
      ElMessage.success(
        isCurrent ? "固件版本已设为历史版本" : "固件版本已设为当前版本"
      );
      // 刷新固件列表以获取最新状态
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "设置失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("设置固件为当前版本失败:", error);
      ElMessage.error("设置失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 删除固件
const deleteFirmware = async (firmware: {
  version: string;
  id: string;
  status: string;
}) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除版本 ${firmware.version} 吗？`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    loading.value = true;

    // 调用后台API删除固件
    const response = await DataService.deleteFirmware(Number(firmware.id));

    if (response && response.data && response.data.code === 0) {
      ElMessage.success("固件已删除");
      // 刷新固件列表
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "删除失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除固件失败:", error);
      ElMessage.error("删除失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 升级设备
const upgradeDevice = async (device: {
  deviceName: string;
  device_id: string;
  currentVersion?: string;
}) => {
  if (!selectedFirmware.value) {
    ElMessage.warning("请先在上方选择目标固件版本");
    return;
  }

  const targetVersion = selectedFirmware.value.version;
  const currentVersion = device.currentVersion || "";
  const cmp = compareVersion(currentVersion, targetVersion);
  if (cmp === 0) {
    ElMessage.info(
      `设备 ${
        device.deviceName || device.device_id
      } 当前已是版本 ${currentVersion}`
    );
    return;
  }

  const isDowngrade = cmp > 0;
  const confirmMessage = isDowngrade
    ? `当前版本为 ${currentVersion}，目标版本为 ${targetVersion} ，将执行降级操作。\n确定要将设备 ${
        device.deviceName || device.device_id
      } 从 ${currentVersion} 降级到 ${targetVersion} 吗？`
    : `确定要将设备 ${
        device.deviceName || device.device_id
      } 升级到版本 ${targetVersion} 吗？`;

  try {
    await ElMessageBox.confirm(confirmMessage, "确认升级", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    });

    await startUpgradeForDevices([device.device_id]);
  } catch (error) {
    if (error !== "cancel") {
      logger.error("单设备升级失败", error);
    }
  }
};

// 取消升级
const cancelUpgrade = async (device: {
  deviceName: string;
  device_id: string;
}) => {
  const deviceLabel = device.deviceName || device.device_id;
  try {
    await ElMessageBox.confirm(
      `确定要取消设备 ${deviceLabel} 的升级吗？`,
      "确认取消",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    loading.value = true;
    const response = await DataService.cancelOTADeviceUpgrade(device.device_id);

    if (response && response.data && response.data.code === 0) {
      ElMessage.success("升级已取消");
      await fetchOTAData();
    } else {
      ElMessage.error(response?.data?.message || "取消升级失败");
    }
  } catch (error) {
    if (error !== "cancel") {
      logger.error("取消升级失败", error);
      ElMessage.error("取消升级失败，请稍后重试");
    }
  } finally {
    loading.value = false;
  }
};

// 查看升级日志
const viewUpgradeLog = (device: { deviceName: string }) => {
  ElMessage.info(`查看设备 ${device.deviceName} 的升级日志`);
};

// 重置上传表单
const resetUploadForm = () => {
  uploadForm.value = {
    version: "",
    description: "",
    file: null,
  };
  fileList.value = [];
  if (uploadFormRef.value) {
    uploadFormRef.value.resetFields();
  }
};

// 处理取消上传
const handleCancelUpload = () => {
  showUploadDialog.value = false;
  resetUploadForm();
};

// 音频上传逻辑
const resetAudioUploadForm = () => {
  audioUploadForm.value = { audioType: "alarm", file: null };
  audioFileList.value = [];
};

const openAudioUploadDialog = (audioType?: string) => {
  if (audioType && audioTypeOptions.some((item) => item.value === audioType)) {
    audioUploadForm.value.audioType = audioType;
  }
  showAudioUploadDialog.value = true;
};

const handleAudioFileRemove = () => {
  audioUploadForm.value.file = null;
  audioFileList.value = [];
};

const handleAudioFileChange = (file: any) => {
  const raw = file.raw as File | undefined;
  if (!raw) {
    handleAudioFileRemove();
    return;
  }
  if (!raw.name.toLowerCase().endsWith(".mp3")) {
    ElMessage.error("仅支持 .mp3 文件");
    handleAudioFileRemove();
    return;
  }
  if (!ASCII_FILENAME_REGEX.test(raw.name)) {
    ElMessage.error("文件名仅允许 ASCII 字符，不能包含空格和中文");
    handleAudioFileRemove();
    return;
  }
  if (raw.size > 300 * 1024) {
    ElMessage.error("文件大小不得超过 300KB");
    handleAudioFileRemove();
    return;
  }
  audioUploadForm.value.file = raw;
  audioFileList.value = [file];
};

const cancelAudioUpload = () => {
  showAudioUploadDialog.value = false;
  resetAudioUploadForm();
};

const uploadAudioAsset = async () => {
  if (!audioUploadForm.value.audioType) {
    ElMessage.warning("请选择音频类型");
    return;
  }
  if (!audioUploadForm.value.file) {
    ElMessage.warning("请先选择 mp3 文件");
    return;
  }
  const formData = new FormData();
  formData.append("file", audioUploadForm.value.file);

  audioUploadLoading.value = true;
  try {
    const response = await DataService.uploadOTAAudio(
      audioUploadForm.value.audioType,
      formData
    );
    if (response && response.data && response.data.code === 0) {
      ElMessage.success("音频上传成功");
      showAudioUploadDialog.value = false;
      resetAudioUploadForm();
      await fetchAudioAssets();
    } else {
      ElMessage.error(response?.data?.message || "上传失败");
    }
  } catch (error) {
    logger.error("上传音频失败:", error);
    ElMessage.error("上传失败，请稍后重试");
  } finally {
    audioUploadLoading.value = false;
  }
};

// 处理文件变化
const handleFileChange = (file: any) => {
  uploadForm.value.file = file.raw;

  // 自动解析文件名中的版本号
  const fileName = file.name;
  console.log("上传文件:", fileName);

  // 匹配kangyang_x.x.x.bin格式
  const kangyangVersionMatch = fileName.match(
    /kangyang_([v]?\d+\.\d+(?:\.\d+)?)\.bin/i
  );
  if (kangyangVersionMatch) {
    let versionStr = kangyangVersionMatch[1];
    // 确保版本号格式正确
    if (!versionStr.startsWith("v")) {
      versionStr = `v${versionStr}`;
    }
    uploadForm.value.version = versionStr;
    console.log("从文件名解析版本号:", versionStr);
    ElMessage.success(`自动解析版本号: ${versionStr}`);
    return;
  }

  // 匹配其他常见的版本号格式
  const versionMatch = fileName.match(/[v]?(\d+\.\d+(?:\.\d+)?)/);
  if (versionMatch) {
    let versionStr = versionMatch[0];
    if (!versionStr.startsWith("v")) {
      versionStr = `v${versionStr}`;
    }
    uploadForm.value.version = versionStr;
    console.log("从文件名解析版本号:", versionStr);
    ElMessage.success(`自动解析版本号: ${versionStr}`);
    return;
  }

  // 如果没有解析到版本号，清空版本号字段
  uploadForm.value.version = "";
  console.log("未从文件名解析到版本号");
};

// 上传固件
const uploadFirmware = async () => {
  uploadFormRef.value.validate(async (valid: boolean) => {
    if (valid) {
      try {
        loading.value = true;

        // 创建FormData
        const formData = new FormData();
        if (uploadForm.value.file) {
          formData.append("file", uploadForm.value.file);
        }
        formData.append("description", uploadForm.value.description);
        formData.append("release_notes", ""); // 暂时为空
        formData.append("compatibility", "ESP32-S3"); // 默认兼容性

        // 调用API上传固件
        const response = await DataService.uploadFirmware(formData);

        if (response && response.data && response.data.code === 0) {
          ElMessage.success("固件上传成功");
          showUploadDialog.value = false;

          // 重置表单
          resetUploadForm();

          // 刷新固件列表
          await fetchOTAData();
        } else {
          ElMessage.error(response?.data?.message || "上传失败");
        }
      } catch (error) {
        console.error("上传固件失败:", error);
        ElMessage.error("上传失败，请稍后重试");
      } finally {
        loading.value = false;
      }
    }
  });
};

// 处理数据源变化
const handleDataSourceChange = () => {
  logger.info("🔄 检测到数据源变化，重新加载OTA数据");
  fetchOTAData();
};

// 搜索功能
const handleSearch = () => {
  fetchOTAData();
};

// 刷新数据
const refreshData = () => {
  logger.info("🔄 手动刷新OTA数据");
  fetchOTAData();
};

onMounted(() => {
  fetchOTAData();
  fetchAudioAssets(); // 添加这行，页面加载时查询音频资源

  // 监听数据源变化事件
  window.addEventListener("dataSourceChanged", handleDataSourceChange);
});

onUnmounted(() => {
  window.removeEventListener("dataSourceChanged", handleDataSourceChange);
  stopOtaPolling();
  cancelKeywordDebounce();
});

watch(firmwareList, (list) => {
  if (
    selectedFirmwareId.value !== null &&
    !list.some((fw) => Number(fw.id) === Number(selectedFirmwareId.value))
  ) {
    selectedFirmwareId.value = null;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_radio = _resolveComponent("el-radio")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_card = _resolveComponent("el-card")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_progress = _resolveComponent("el-progress")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_upload = _resolveComponent("el-upload")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_card, { class: "section-card" }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _cache[13] || (_cache[13] = _createElementVNode("span", null, "固件版本管理", -1)),
            _createElementVNode("div", _hoisted_4, [
              (hasPermission('ota.manage'))
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 0,
                    type: "primary",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (showUploadDialog.value = true))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Upload))
                        ]),
                        _: 1
                      }),
                      _cache[11] || (_cache[11] = _createTextVNode(" 上传固件 ", -1))
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (hasPermission('ota.manage'))
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 1,
                    link: "",
                    onClick: refreshFirmware
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[12] || (_cache[12] = _createTextVNode(" 刷新 ", -1))
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_el_table, {
            ref_key: "firmwareTableRef",
            ref: firmwareTableRef,
            data: firmwareList.value,
            style: { width: '100%' },
            "highlight-current-row": "",
            onCurrentChange: handleFirmwareSelectionChange,
            "row-class-name": getFirmwareRowClass
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                label: "选择",
                width: "80",
                align: "center"
              }, {
                default: _withCtx((scope) => [
                  _createVNode(_component_el_radio, {
                    "model-value": selectedFirmwareId.value ?? undefined,
                    label: Number(scope.row.id),
                    onChange: () => handleFirmwareSelectionChange(scope.row)
                  }, null, 8, ["model-value", "label", "onChange"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "version",
                label: "版本号",
                width: "120"
              }),
              _createVNode(_component_el_table_column, {
                prop: "description",
                label: "版本描述"
              }),
              _createVNode(_component_el_table_column, {
                prop: "size",
                label: "文件大小",
                width: "100"
              }),
              _createVNode(_component_el_table_column, {
                prop: "uploadTime",
                label: "上传时间",
                width: "160"
              }),
              _createVNode(_component_el_table_column, {
                prop: "status",
                label: "状态",
                width: "100"
              }, {
                default: _withCtx((scope) => [
                  _createVNode(_component_el_tag, {
                    type: scope.row.status === 'latest' ? 'success' : 'info'
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(scope.row.status === "latest" ? "当前版本" : "历史版本"), 1)
                    ]),
                    _: 2
                  }, 1032, ["type"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                label: "操作",
                width: "200"
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("div", _hoisted_5, [
                    (hasPermission('ota.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          type: "primary",
                          size: "small",
                          onClick: ($event: any) => (setActiveFirmware(scope.row))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(scope.row.status === "latest" ? "设为历史" : "设为当前"), 1)
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      : _createCommentVNode("", true),
                    (hasPermission('ota.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 1,
                          type: "danger",
                          size: "small",
                          onClick: ($event: any) => (deleteFirmware(scope.row))
                        }, {
                          default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                            _createTextVNode(" 删除 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["onClick"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["data"])
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_component_el_card, { class: "section-card" }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_7, [
            _cache[17] || (_cache[17] = _createElementVNode("span", null, "音频资源管理", -1)),
            (hasPermission('ota.manage'))
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_component_el_button, {
                    type: "primary",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (openAudioUploadDialog()))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Upload))
                        ]),
                        _: 1
                      }),
                      _cache[15] || (_cache[15] = _createTextVNode(" 上传音频 ", -1))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_el_button, {
                    link: "",
                    onClick: fetchAudioAssets
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_icon, null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Refresh))
                        ]),
                        _: 1
                      }),
                      _cache[16] || (_cache[16] = _createTextVNode(" 刷新 ", -1))
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_el_alert, {
            type: "info",
            "show-icon": "",
            class: "audio-tip",
            title: "仅支持 .mp3 文件，大小 ≤ 300KB，文件名为大小写字母加数字组成。"
          }),
          _withDirectives((_openBlock(), _createBlock(_component_el_table, {
            data: audioTableData.value,
            style: { width: '100%', marginTop: '16px' },
            "empty-text": "暂无音频资源"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                prop: "label",
                label: "音频类型",
                width: "140"
              }),
              _createVNode(_component_el_table_column, { label: "文件信息" }, {
                default: _withCtx((scope) => [
                  (scope.row.asset)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(scope.row.asset.filename), 1),
                          _createVNode(_component_el_tag, { size: "small" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(formatFileSize(scope.row.asset.file_size)), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _createElementVNode("div", _hoisted_12, [
                          _createElementVNode("span", null, "MD5：" + _toDisplayString(scope.row.asset.file_md5), 1),
                          _createElementVNode("span", null, "上传：" + _toDisplayString(formatUploadTime(scope.row.asset.uploaded_at)), 1)
                        ]),
                        (scope.row.asset.download_url)
                          ? (_openBlock(), _createBlock(_component_el_link, {
                              key: 0,
                              type: "primary",
                              href: scope.row.asset.download_url,
                              target: "_blank"
                            }, {
                              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                _createTextVNode(" 下载链接 ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["href"]))
                          : _createCommentVNode("", true)
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_13, "未上传"))
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                label: "操作",
                width: "160",
                align: "center"
              }, {
                default: _withCtx((scope) => [
                  (hasPermission('ota.manage'))
                    ? (_openBlock(), _createBlock(_component_el_button, {
                        key: 0,
                        type: "primary",
                        text: "",
                        size: "small",
                        onClick: ($event: any) => (openAudioUploadDialog(scope.row.value))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(scope.row.asset ? "替换" : "上传"), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["data"])), [
            [_directive_loading, audioLoading.value]
          ])
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_14, [
      _createVNode(_component_el_card, { class: "section-card" }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_15, [
            _cache[20] || (_cache[20] = _createElementVNode("span", null, "设备升级状态", -1)),
            _createElementVNode("div", _hoisted_16, [
              _createVNode(_component_el_select, {
                modelValue: statusFilter.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((statusFilter).value = $event)),
                placeholder: "筛选状态",
                style: {"width":"120px"},
                onChange: handleFilterChanged
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_option, {
                    label: "全部",
                    value: ""
                  }),
                  _createVNode(_component_el_option, {
                    label: "最新版本",
                    value: "latest"
                  }),
                  _createVNode(_component_el_option, {
                    label: "待升级",
                    value: "pending"
                  }),
                  _createVNode(_component_el_option, {
                    label: "需要升级",
                    value: "outdated"
                  }),
                  _createVNode(_component_el_option, {
                    label: "升级中",
                    value: "upgrading"
                  }),
                  _createVNode(_component_el_option, {
                    label: "升级失败",
                    value: "failed"
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_component_el_input, {
                modelValue: searchKeyword.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchKeyword).value = $event)),
                placeholder: "搜索设备",
                style: {"width":"200px"},
                clearable: "",
                onInput: handleKeywordInput,
                onClear: handleKeywordCleared
              }, {
                prefix: _withCtx(() => [
                  _createVNode(_component_el_icon, null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Search))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"]),
              (hasPermission('ota.manage'))
                ? (_openBlock(), _createBlock(_component_el_button, {
                    key: 0,
                    type: "primary",
                    onClick: handleTargetedUpgrade,
                    disabled: !canStartTargetedUpgrade.value
                  }, {
                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                      _createTextVNode(" 对选中设备升级到所选版本 ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_el_table, {
            ref_key: "deviceTableRef",
            ref: deviceTableRef,
            data: filteredDeviceList.value,
            style: { width: '100%' },
            onSelectionChange: handleDeviceSelectionChange
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                type: "selection",
                width: "55"
              }),
              _createVNode(_component_el_table_column, {
                prop: "device_id",
                label: "设备编号",
                width: "180"
              }),
              _createVNode(_component_el_table_column, {
                prop: "deviceName",
                label: "设备名称",
                width: "160"
              }),
              _createVNode(_component_el_table_column, {
                prop: "currentVersion",
                label: "当前版本",
                width: "120"
              }),
              _createVNode(_component_el_table_column, {
                label: "目标版本",
                width: "120"
              }, {
                default: _withCtx((scope) => [
                  _createTextVNode(_toDisplayString(getDeviceTargetVersion(scope.row)), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "upgradeStatus",
                label: "升级状态",
                width: "120"
              }, {
                default: _withCtx((scope) => [
                  _createVNode(_component_el_tag, {
                    type: getStatusType(scope.row.upgradeStatus)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getStatusText(scope.row.upgradeStatus)), 1)
                    ]),
                    _: 2
                  }, 1032, ["type"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "upgradeProgress",
                label: "升级进度",
                width: "120"
              }, {
                default: _withCtx((scope) => [
                  (scope.row.upgradeStatus === 'upgrading')
                    ? (_openBlock(), _createBlock(_component_el_progress, {
                        key: 0,
                        percentage: scope.row.upgradeProgress,
                        "stroke-width": 8,
                        "show-text": false
                      }, null, 8, ["percentage"]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(scope.row.upgradeProgress) + "%", 1))
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                prop: "lastUpgradeTime",
                label: "最后升级时间",
                width: "160"
              }),
              _createVNode(_component_el_table_column, {
                label: "操作",
                width: "200"
              }, {
                default: _withCtx((scope) => [
                  _createElementVNode("div", _hoisted_18, [
                    (hasPermission('ota.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          type: "primary",
                          size: "small",
                          disabled: scope.row.upgradeStatus === 'upgrading',
                          onClick: ($event: any) => (upgradeDevice(scope.row))
                        }, {
                          default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                            _createTextVNode(" 升级 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled", "onClick"]))
                      : _createCommentVNode("", true),
                    (hasPermission('ota.manage'))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 1,
                          type: "warning",
                          size: "small",
                          disabled: scope.row.upgradeStatus !== 'upgrading',
                          onClick: ($event: any) => (cancelUpgrade(scope.row))
                        }, {
                          default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                            _createTextVNode(" 取消 ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled", "onClick"]))
                      : _createCommentVNode("", true),
                    _createVNode(_component_el_button, {
                      type: "info",
                      size: "small",
                      onClick: ($event: any) => (viewUpgradeLog(scope.row))
                    }, {
                      default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                        _createTextVNode(" 日志 ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["onClick"])
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["data"]),
          _createElementVNode("div", _hoisted_19, [
            _createVNode(_component_el_pagination, {
              "current-page": currentPage.value,
              "onUpdate:currentPage": _cache[4] || (_cache[4] = ($event: any) => ((currentPage).value = $event)),
              "page-size": pageSize.value,
              "onUpdate:pageSize": _cache[5] || (_cache[5] = ($event: any) => ((pageSize).value = $event)),
              "page-sizes": [10, 20, 50, 100],
              total: deviceList.value.length,
              layout: "total, sizes, prev, pager, next, jumper"
            }, null, 8, ["current-page", "page-size", "total"])
          ])
        ]),
        _: 1
      })
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: showUploadDialog.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showUploadDialog).value = $event)),
      title: "上传固件",
      width: "500px",
      onClose: resetUploadForm
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_20, [
          _createVNode(_component_el_button, { onClick: handleCancelUpload }, {
            default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          (hasPermission('ota.manage'))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                onClick: uploadFirmware
              }, {
                default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                  _createTextVNode("上传", -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          model: uploadForm.value,
          rules: uploadRules,
          ref_key: "uploadFormRef",
          ref: uploadFormRef,
          "label-width": "80px",
          "label-position": "left"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, {
              label: "版本号",
              prop: "version"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: uploadForm.value.version,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((uploadForm.value.version) = $event)),
                  placeholder: "请输入版本号，如：v1.2.3"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "版本描述",
              prop: "description"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_input, {
                  modelValue: uploadForm.value.description,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((uploadForm.value.description) = $event)),
                  type: "textarea",
                  rows: 3,
                  placeholder: "请输入版本描述"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, {
              label: "固件文件",
              prop: "file"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_upload, {
                  class: "upload-demo",
                  drag: "",
                  "auto-upload": false,
                  "on-change": handleFileChange,
                  "file-list": fileList.value,
                  accept: ".bin,.hex,.fw"
                }, {
                  tip: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                    _createElementVNode("div", { class: "el-upload__tip" }, " 只能上传 .bin/.hex/.fw 文件，且不超过 50MB ", -1)
                  ]))]),
                  default: _withCtx(() => [
                    _createVNode(_component_el_icon, { class: "el-icon--upload" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(UploadFilled))
                      ]),
                      _: 1
                    }),
                    _cache[25] || (_cache[25] = _createElementVNode("div", { class: "el-upload__text" }, [
                      _createTextVNode(" 将文件拖到此处，或"),
                      _createElementVNode("em", null, "点击上传")
                    ], -1))
                  ]),
                  _: 1
                }, 8, ["file-list"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_el_dialog, {
      modelValue: showAudioUploadDialog.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showAudioUploadDialog).value = $event)),
      title: "上传音频资源",
      width: "460px",
      onClose: resetAudioUploadForm
    }, {
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_21, [
          _createVNode(_component_el_button, { onClick: cancelAudioUpload }, {
            default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
              _createTextVNode("取消", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: uploadAudioAsset,
            loading: audioUploadLoading.value
          }, {
            default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
              _createTextVNode(" 上传 ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_form, {
          model: audioUploadForm.value,
          "label-width": "90px",
          "label-position": "left"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_form_item, { label: "音频类型" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_select, {
                  modelValue: audioUploadForm.value.audioType,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((audioUploadForm.value.audioType) = $event)),
                  placeholder: "请选择音频类型"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(audioTypeOptions, (item) => {
                      return _createVNode(_component_el_option, {
                        key: item.value,
                        label: item.label,
                        value: item.value
                      }, null, 8, ["label", "value"])
                    }), 64))
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_component_el_form_item, { label: "音频文件" }, {
              default: _withCtx(() => [
                _createVNode(_component_el_upload, {
                  class: "upload-audio",
                  drag: "",
                  accept: ".mp3",
                  "auto-upload": false,
                  "file-list": audioFileList.value,
                  "on-change": handleAudioFileChange,
                  "on-remove": handleAudioFileRemove
                }, {
                  tip: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                    _createElementVNode("div", { class: "el-upload__tip" }, " 文件名需 ASCII 字符，大小 ≤ 300KB，仅支持 .mp3 ", -1)
                  ]))]),
                  default: _withCtx(() => [
                    _createVNode(_component_el_icon, { class: "el-icon--upload" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(UploadFilled))
                      ]),
                      _: 1
                    }),
                    _cache[29] || (_cache[29] = _createElementVNode("div", { class: "el-upload__text" }, [
                      _createTextVNode(" 拖拽到此处，或"),
                      _createElementVNode("em", null, "点击选择 mp3 文件")
                    ], -1))
                  ]),
                  _: 1
                }, 8, ["file-list"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})